import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faShoppingBag } from "@fortawesome/pro-light-svg-icons";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IOnlineStoreActions } from "redux/actions/customer/onlineStore";
import { IOnlineStoreState } from "redux/reducers/customer/onlineStore";
import "./StoreHeader.scss";
import CustomDropdown from "components/customDropdown/CustomDropdown";
import CustomDropdownOption from "components/customDropdown/CustomDropdownOption";
import { IFacility } from "../../models/facility";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import GoogleAnalytics from "components/googleAnalytics/GoogleAnalytics";

interface IStoreHeaderProps {
  onlineStoreStore: IOnlineStoreState;
  onlineStoreActions: IOnlineStoreActions;
  selectedFacility?: IFacility;
  facilityShortName: string;
  facilityLogoSource: string;
  shoppingBagHasItems: boolean;
  clientFacilities?: IFacility[];
}

export default function StoreHeader(props: IStoreHeaderProps) {
  const {
    onlineStoreStore,
    onlineStoreActions,
    selectedFacility,
    facilityShortName,
    facilityLogoSource,
    shoppingBagHasItems,
    clientFacilities,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const windowSize = useWindowSize();

  const showSelectedFacility = selectedFacility && clientFacilities?.length > 0;

  const [facilitySearchQuery, setFacilitySearchQuery] = useState<string>("");

  function navigateToHomePage() {
    const newPath = `/online-store/${facilityShortName}`;

    if (location.pathname !== newPath) {
      history.push(newPath);
    }
  }

  function navigateToCart() {
    history.push(`/online-store/${facilityShortName}/shoppingBag`);
  }

  function navigateToFacility(shortName: string) {
    if (selectedFacility.short_name !== shortName) {
      window.location.href = `/online-store/${shortName}`;
    }
  }

  function handleProductSearchQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
    onlineStoreActions.updateProductSearchQuery(e.target.value);
  }

  function handleProductSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      navigateToHomePage();
    }
  }

  function facilityMatchesSearchQuery(facility: IFacility) {
    const upperCasedSearchQuery = facilitySearchQuery.toUpperCase();
    const upperCasedLongName = facility.long_name.toUpperCase();
    return upperCasedLongName.indexOf(upperCasedSearchQuery) > -1;
  }

  return (
    <div className="online-store-header">
      <GoogleAnalytics
        title={selectedFacility ? `${selectedFacility?.long_name} Online Store` : undefined}
        tagID={selectedFacility?.google_analytics_code ?? undefined}
      />
      <div className="online-store-header-navigation">
        <button onClick={navigateToHomePage}>
          <img
            className="online-store-header-facility-logo"
            style={{
              display: showSelectedFacility && windowSize.width <= MOBILE_WIDTH ? "none" : "block",
            }}
            src={facilityLogoSource}
            alt="Facility Logo" // TODO: Translate
          />
        </button>
        {showSelectedFacility && (
          <CustomDropdown
            parentElement={
              <div>
                <div className="online-store-header-selected-facility-name">{selectedFacility.long_name}</div>
                <div className="online-store-header-selected-facility-secondary-information">
                  {`${selectedFacility.address_line_1}, ${selectedFacility.city}, ${selectedFacility.province_name}, ${selectedFacility.postal}`}
                </div>
                {selectedFacility.address_line_2 && (
                  <div className="online-store-header-selected-facility-secondary-information">
                    {selectedFacility.address_line_2}
                  </div>
                )}
              </div>
            }
            onChange={navigateToFacility}
            defaultValue={selectedFacility.short_name}
            onSearch={setFacilitySearchQuery}
            searchPlacerHolder="Search facilities..." // TODO: Translate
            hideDropdowns={clientFacilities.length === 1 ? true : false}
          >
            {clientFacilities
              .filter(facilityMatchesSearchQuery)
              ?.sort((a: IFacility, b: IFacility) => a.full_name.localeCompare(b.full_name))
              ?.map(facility => {
                return (
                  <CustomDropdownOption value={facility.short_name} key={facility.id}>
                    {facility.long_name}
                  </CustomDropdownOption>
                );
              })}
          </CustomDropdown>
        )}
      </div>
      <div className="online-store-header-filter-container">
        <span className="online-store-header-search-input-container">
          <span className="online-store-header-search-icon">
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </span>
          <input
            className="online-store-header-search-input"
            placeholder="Search" // TODO: Translate
            value={onlineStoreStore.productSearchQuery}
            onChange={handleProductSearchQueryChange}
            onKeyDown={handleProductSearchKeyDown}
          />
        </span>
        <button className="online-store-header-shopping-bag" onClick={navigateToCart}>
          <FontAwesomeIcon icon={faShoppingBag}></FontAwesomeIcon>
        </button>
        {shoppingBagHasItems && <span className="online-store-header-shopping-bag-dot"></span>}
      </div>
    </div>
  );
}
